/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { shallowEqual, useSelector } from "react-redux";
import { BlockTypes } from "constants/BlockTypes";
import useBlocks from "selectors/BlocksSelector";
import { useUsedAssets } from "selectors/AssetSelector";
import TranscriptionState from "constants/TranscriptionState";
import { RecordingSessionProcessingStates } from "constants/RecordingSessionProcessingStates";
import { Heading16 } from "components/shared/Heading";
import { DebugPanelStat } from "./DebugPanelStat";

export const ProjectStatsDebugSection: React.FC = () => {
  // If the project is not loaded, return null

  const { projectId, clips, assetsMap, recordingSessions } = useSelector(
    (state) => {
      return {
        projectId: state.project?.id,
        clips: state.clips,
        assetsMap: state.assets,
        recordingSessions: Object.values(state.recordingSessions),
      };
    },
    shallowEqual
  );

  // Clips stats
  const numWavClips = clips.filter(
    (clip) => clip.type == BlockTypes.WAVEFORM
  ).length;
  const numTxnClips = clips.filter(
    (clip) => clip.type == BlockTypes.TRANSCRIPTION
  ).length;
  const numPlaceClips = clips.filter(
    (clip) => clip.type == BlockTypes.PLACEHOLDER
  ).length;
  const numRecClips = clips.filter(
    (clip) => clip.type == BlockTypes.RECORDING_SESSION
  ).length;

  // Block stats
  const blocks = useBlocks();
  const numWavBlocks = blocks.filter(
    (block) => block.type == BlockTypes.WAVEFORM
  ).length;
  const numTxnBlocks = blocks.filter(
    (block) => block.type == BlockTypes.TRANSCRIPTION
  ).length;
  const numPlaceBlocks = blocks.filter(
    (block) => block.type == BlockTypes.PLACEHOLDER
  ).length;
  const numRecBlocks = blocks.filter(
    (block) => block.type == BlockTypes.RECORDING_SESSION
  ).length;

  // Asset stats
  const assets = Object.values(assetsMap);
  const usedAssetsMap = useUsedAssets();
  const usedAssets = Object.values(usedAssetsMap);
  const unusedAssets = assets.filter((asset) => !(asset.id in usedAssetsMap));
  const numUsedEnhancedAssets = usedAssets.filter(
    (asset) => !!asset.originalTrackId
  ).length;
  const numUnusedEnhancedAssets = unusedAssets.filter(
    (asset) => !!asset.originalTrackId
  ).length;
  const numUsedOrigAssets = usedAssets.length - numUsedEnhancedAssets;
  const numUnusedOrigAssets = unusedAssets.length - numUnusedEnhancedAssets;
  const numOrigAssets = numUsedOrigAssets + numUnusedOrigAssets;
  const numEnhancedAssets = numUsedEnhancedAssets + numUnusedEnhancedAssets;

  // Transcript stats
  const numPendingTxpts = assets.filter(
    (asset) =>
      !asset.originalTrackId && // Don't count enhanced tracks since they can't be transcribed
      asset.transcriptionState == TranscriptionState.PENDING
  ).length;
  const numFailedTxpts = assets.filter(
    (asset) => asset.transcriptionState == TranscriptionState.FAILED
  ).length;
  const numCompleteTxpts = assets.filter(
    (asset) => asset.transcriptionState == TranscriptionState.COMPLETE
  ).length;
  const numProcessingTxpts = assets.filter(
    (asset) => asset.transcriptionState == TranscriptionState.PROCESSING
  ).length;
  const txptServiceCounts: Record<string, number> = {};
  assets.forEach((asset) => {
    if (asset.transcriptionState !== TranscriptionState.COMPLETE) {
      return;
    }

    if (asset.transcriptionService) {
      txptServiceCounts[asset.transcriptionService] =
        (txptServiceCounts[asset.transcriptionService] || 0) + 1;
    } else {
      txptServiceCounts["not specified"] =
        (txptServiceCounts["not specified"] || 0) + 1;
    }
  });

  // Recording Session stats
  const numPendingSessions = recordingSessions.filter(
    (session) =>
      session.processingState == RecordingSessionProcessingStates.PENDING
  ).length;
  const numProcessingSessions = recordingSessions.filter(
    (session) =>
      session.processingState == RecordingSessionProcessingStates.PROCESSING
  ).length;
  const numFailedSessions = recordingSessions.filter(
    (session) =>
      session.processingState == RecordingSessionProcessingStates.FAILED
  ).length;
  const numCompleteSessions = recordingSessions.filter(
    (session) =>
      session.processingState == RecordingSessionProcessingStates.COMPLETE
  ).length;

  if (!projectId) {
    return null;
  }

  return (
    <div>
      <hr />
      <Heading16>Project Stats</Heading16>
      <DebugPanelStat label="Project ID" value={projectId} />
      <DebugPanelStat
        label="# Clips (tot/wav/txn/place/rec)"
        value={`${clips.length}/${numWavClips}/${numTxnClips}/${numPlaceClips}/${numRecClips}`}
      />
      <DebugPanelStat
        label="# Blocks (tot/wav/txn/place/rec)"
        value={`${blocks.length}/${numWavBlocks}/${numTxnBlocks}/${numPlaceBlocks}/${numRecBlocks}`}
      />
      <DebugPanelStat
        label="# Recording Sessions (tot/pend/proc/complete/fail)"
        value={`${recordingSessions.length}/${numPendingSessions}/${numProcessingSessions}/${numCompleteSessions}/${numFailedSessions}`}
      />
      <DebugPanelStat
        label="# Assets (tot/orig/enhanced)"
        value={`${assets.length}/${numOrigAssets}/${numEnhancedAssets}`}
      />
      <DebugPanelStat
        label="# Used Assets (tot/orig/enhanced)"
        value={`${usedAssets.length}/${numUsedOrigAssets}/${numUsedEnhancedAssets}`}
      />
      <DebugPanelStat
        label="# Unused Assets (tot/orig/enhanced)"
        value={`${unusedAssets.length}/${numUnusedOrigAssets}/${numUnusedEnhancedAssets}`}
      />
      <DebugPanelStat
        label="# Asset Transcriptions (proc/pend/complete/fail)"
        value={`${numProcessingTxpts}/${numPendingTxpts}/${numCompleteTxpts}/${numFailedTxpts}`}
      />
      <DebugPanelStat
        label="# Transcriptions by service"
        value={`${JSON.stringify(txptServiceCounts)}`}
      />
    </div>
  );
};
